import React, { useState, useEffect } from 'react';
import { 
  Edit, 
  Eye, 
  EyeOff, 
  Trash2, 
  Upload,
  CheckCircle,
  AlertCircle,
  User,
  Settings,
  Camera
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { Profile } from '../../types';
import apiService from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const MyProfile: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [profileStatus, setProfileStatus] = useState<'online' | 'offline'>('online');

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const response = await apiService.getMyProfile();
      setProfile(response.profile);
      setProfileStatus(response.profile?.isActive ? 'online' : 'offline');
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!profile) return;
    
    try {
      const newStatus = !profile.isActive;
      await apiService.toggleProfileStatus(newStatus);
      setProfile(prev => prev ? { ...prev, isActive: newStatus } : null);
      setProfileStatus(newStatus ? 'online' : 'offline');
    } catch (error) {
      console.error('Failed to toggle profile status:', error);
      alert('Failed to update profile status');
    }
  };

  const handleDeleteProfile = async () => {
    try {
      await apiService.deleteProfile();
      await refreshUser();
      alert('Profile deleted successfully');
    } catch (error) {
      console.error('Failed to delete profile:', error);
      alert('Failed to delete profile');
    }
  };

  const getVerificationStatus = () => {
    if (!user) return null;
    
    switch (user.verificationStatus) {
      case 'verified':
        return (
          <div className="flex items-center space-x-2 text-green-600 bg-green-50 px-3 py-1 rounded-full">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Verified</span>
          </div>
        );
      case 'semi-verified':
        return (
          <div className="flex items-center space-x-2 text-yellow-600 bg-yellow-50 px-3 py-1 rounded-full">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Semi-verified</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center space-x-2 text-red-600 bg-red-50 px-3 py-1 rounded-full">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Not Verified</span>
          </div>
        );
    }
  };

  const formatText = (text: string) => {
    return text?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
  };

  if (loading) {
    return <LoadingSpinner fullScreen text="Loading your profile..." />;
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Profile not found</h2>
        <p className="text-gray-600">There was an issue loading your profile.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">My Profile</h1>
          <p className="text-gray-600">Manage your profile information and settings</p>
        </div>
        
        <div className="flex items-center space-x-4 mt-4 md:mt-0">
          {getVerificationStatus()}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Profile Picture & Actions */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            {/* Profile Picture */}
            <div className="text-center mb-6">
              {profile.photos.length > 0 ? (
                <div className="relative inline-block">
                  <img
                    src={profile.photos[0]}
                    alt={profile.name}
                    className="w-32 h-32 rounded-full object-cover mx-auto"
                  />
                  <button className="absolute bottom-0 right-0 bg-primary-600 text-white p-2 rounded-full hover:bg-primary-700 transition-colors">
                    <Camera className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="w-32 h-32 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-16 h-16 text-primary-600" />
                </div>
              )}
              
              <h2 className="text-xl font-semibold text-gray-900 mt-4">{profile.name}</h2>
              <p className="text-gray-600">{profile.age} years old</p>
            </div>

            {/* Status Toggle */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Profile Status</span>
                <button
                  onClick={handleToggleStatus}
                  className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                    profileStatus === 'online'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {profileStatus === 'online' ? (
                    <>
                      <Eye className="w-3 h-3" />
                      <span>Online</span>
                    </>
                  ) : (
                    <>
                      <EyeOff className="w-3 h-3" />
                      <span>Offline</span>
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500">
                {profileStatus === 'online' 
                  ? 'Your profile is visible to others'
                  : 'Your profile is hidden from search'
                }
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={() => setEditing(true)}
                className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2"
              >
                <Edit className="w-4 h-4" />
                <span>Edit Profile</span>
              </button>
              
              <button className="w-full border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2">
                <Upload className="w-4 h-4" />
                <span>Upload Photos</span>
              </button>
              
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="w-full border border-red-300 text-red-600 py-2 px-4 rounded-lg hover:bg-red-50 transition-colors flex items-center justify-center space-x-2"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete Profile</span>
              </button>
            </div>
          </div>
        </div>

        {/* Right Column - Profile Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <User className="w-5 h-5 text-primary-600" />
              <span>Personal Information</span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-500 mb-1">Full Name</label>
                <p className="font-medium">{profile.name}</p>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Age</label>
                <p className="font-medium">{profile.age} years</p>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Gender</label>
                <p className="font-medium capitalize">{profile.gender}</p>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Height</label>
                <p className="font-medium">{profile.height.replace('.', '\'')}"</p>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Marital Status</label>
                <p className="font-medium">{formatText(profile.maritalStatus)}</p>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Complexion</label>
                <p className="font-medium">{formatText(profile.complexion)}</p>
              </div>
            </div>
          </div>

          {/* Caste Information */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Caste Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-gray-500 mb-1">Caste</label>
                <p className="font-medium capitalize">{formatText(profile.caste)}</p>
              </div>
              {profile.subcaste && (
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Sub-caste</label>
                  <p className="font-medium">{profile.subcaste}</p>
                </div>
              )}
              <div>
                <label className="block text-sm text-gray-500 mb-1">Gothra</label>
                <p className="font-medium">{profile.gothra}</p>
              </div>
            </div>
          </div>

          {/* Professional Information */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Professional Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-500 mb-1">Education</label>
                <p className="font-medium">{formatText(profile.education)}</p>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Occupation</label>
                <p className="font-medium">{formatText(profile.occupation)}</p>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Annual Income</label>
                <p className="font-medium">{profile.annualIncome} LPA</p>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Work Location</label>
                <p className="font-medium">
                  {profile.workLocation.type === 'nri' 
                    ? formatText(profile.workLocation.country || '')
                    : `${formatText(profile.workLocation.city || '')}, ${formatText(profile.workLocation.state || '')}`
                  }
                </p>
              </div>
            </div>
          </div>

          {/* About & Expectations */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">About & Expectations</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-500 mb-2">About Me</label>
                <p className="text-gray-700 leading-relaxed">{profile.selfSummary}</p>
              </div>
              
              <div>
                <label className="block text-sm text-gray-500 mb-2">Partner Expectations</label>
                <p className="text-gray-700 leading-relaxed">{profile.expectations}</p>
              </div>
            </div>
          </div>

          {/* Verification Status */}
          {user?.verificationStatus !== 'verified' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-yellow-900 mb-2">Complete Your Verification</h3>
              <p className="text-yellow-700 mb-4">
                Your profile is {user?.verificationStatus === 'semi-verified' ? 'semi-verified' : 'not verified'}. 
                Complete verification to access all features and view other profiles.
              </p>
              <div className="flex space-x-3">
                {user?.verificationStatus === 'not-verified' && (
                  <button className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors">
                    Verify Email & Phone
                  </button>
                )}
                <button className="border border-yellow-600 text-yellow-600 px-4 py-2 rounded-lg hover:bg-yellow-50 transition-colors">
                  Contact Support
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Profile</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete your profile? This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteProfile}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyProfile;